import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allComics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '1', 10);
  const comic = allComics.find((c) => c.id === comicId) || allComics[0];

  const [activeTab, setActiveTab] = useState<'chapters' | 'reviews'>('chapters');

  const chapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    number: comic.chapters - i,
    title: comic.chapters - i > 100
      ? `第${comic.chapters - i}话 - 精彩剧情`
      : `第${comic.chapters - i}话`,
    updateTime: `${i + 1}天前`,
  }));

  const reviews = [
    {
      id: 1,
      user: '漫画迷小张',
      avatar: 'https://readdy.ai/api/search-image?query=A%20minimalist%20warm%20toned%20abstract%20avatar%20icon%2C%20soft%20gradient%20circle%2C%20peach%20and%20cream%20colors%2C%20clean%20simple%20design%2C%20no%20text&width=80&height=80&seq=avatar-01&orientation=squarish',
      rating: 9.5,
      content: '画风真的太精美了！每一帧都可以截图做壁纸。剧情也非常吸引人，人设饱满，感情线细腻不突兀。最难得的是更新速度很快，每周都期待新章节！',
      date: '2026年7月1日',
    },
    {
      id: 2,
      user: '追漫达人阿星',
      avatar: 'https://readdy.ai/api/search-image?query=A%20minimalist%20warm%20toned%20abstract%20avatar%20icon%2C%20soft%20round%20shape%2C%20coral%20and%20gold%20gradient%2C%20clean%20simple%20design%2C%20no%20text&width=80&height=80&seq=avatar-02&orientation=squarish',
      rating: 9.3,
      content: '从第一话就开始追了，作者的分镜功力真的很强，每次高潮部分都让人心跳加速。女主性格讨喜，不是那种傻白甜，期待后续发展！',
      date: '2026年6月28日',
    },
    {
      id: 3,
      user: '深夜漫画屋',
      avatar: 'https://readdy.ai/api/search-image?query=A%20minimalist%20warm%20toned%20abstract%20avatar%20icon%2C%20soft%20circle%2C%20warm%20amber%20and%20rose%20gradient%2C%20clean%20simple%20design%2C%20no%20text&width=80&height=80&seq=avatar-03&orientation=squarish',
      rating: 9.0,
      content: '很不错的韩漫作品，色彩运用非常有层次感。剧情虽然有套路但处理得很自然，配角也都有各自的闪光点。推荐给所有喜欢少女漫画的朋友！',
      date: '2026年6月25日',
    },
  ];

  return (
    <main className="min-h-screen bg-background-50">
      {/* JSON-LD: Article + BreadcrumbList + WebPage */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": comic.title,
        "name": comic.title,
        "author": {
          "@type": "Person",
          "name": comic.author
        },
        "image": comic.cover,
        "dateModified": "2026-07-03",
        "description": comic.description,
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": comic.rating,
          "bestRating": 10,
          "ratingCount": 12580
        },
        "isPartOf": {
          "@type": "WebPage",
          "name": comic.title,
          "url": `https://www.example.com/article/${comic.id}`,
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.example.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": comic.category,
                "item": "https://www.example.com/category/all"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": comic.title,
                "item": `https://www.example.com/article/${comic.id}`
              }
            ]
          }
        }
      })}} />
      <div className="bg-foreground-900">
        <Navbar />
        <div className="pt-20 pb-12 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-300 mb-8">
              <Link to="/" className="hover:text-white transition-colors whitespace-nowrap">首页</Link>
              <span>/</span>
              <Link to={`/category/all`} className="hover:text-white transition-colors whitespace-nowrap">
                {comic.category}
              </Link>
              <span>/</span>
              <span className="text-white whitespace-nowrap">{comic.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-48 md:w-56 flex-shrink-0 mx-auto md:mx-0">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-500/20 text-primary-300 text-xs font-medium mb-3 whitespace-nowrap">
                  {comic.status}
                </span>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-300 mb-5">
                  <span>作者：{comic.author}</span>
                  <span className="text-foreground-600">|</span>
                  <span>分类：{comic.category}</span>
                  <span className="text-foreground-600">|</span>
                  <span className="flex items-center gap-1 text-accent-400">
                    <i className="ri-star-fill text-sm"></i>
                    {comic.rating}
                  </span>
                  <span className="text-foreground-600">|</span>
                  <span>共 {comic.chapters} 话</span>
                </div>

                <p className="text-foreground-400 text-sm leading-relaxed mb-6 max-w-lg">
                  {comic.description}
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <button className="whitespace-nowrap px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer">
                    开始阅读
                  </button>
                  <button className="whitespace-nowrap px-8 py-3 rounded-full bg-foreground-800 text-white font-medium text-sm hover:bg-foreground-700 transition-all cursor-pointer">
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="py-12 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-6 mb-8 border-b border-background-200/70">
            <button
              onClick={() => setActiveTab('chapters')}
              className={`pb-3 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'chapters'
                  ? 'text-primary-600 border-b-2 border-primary-500'
                  : 'text-foreground-400 hover:text-foreground-600'
              }`}
            >
              章节列表
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`pb-3 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'reviews'
                  ? 'text-primary-600 border-b-2 border-primary-500'
                  : 'text-foreground-400 hover:text-foreground-600'
              }`}
            >
              读者评价
            </button>
          </div>

          {activeTab === 'chapters' && (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {chapters.map((chapter) => (
                  <Link
                    key={chapter.number}
                    to="#"
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer border border-background-200/70"
                  >
                    <span className="text-sm text-foreground-800 truncate">
                      {chapter.title}
                    </span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-3">
                      {chapter.updateTime}
                    </span>
                  </Link>
                ))}
              </div>
              {comic.chapters > 20 && (
                <div className="text-center mt-8">
                  <button className="whitespace-nowrap px-6 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer">
                    查看更多章节 ({comic.chapters}话)
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <div className="bg-background-100 rounded-xl p-6 md:p-8 mb-8">
                <div className="flex items-center gap-4 mb-8">
                  <div className="px-4 py-2.5 rounded-full bg-foreground-900 text-white text-lg font-bold flex items-center gap-2 whitespace-nowrap">
                    <i className="ri-star-fill text-accent-400"></i>
                    {comic.rating}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">用户综合评分</p>
                    <p className="text-xs text-foreground-400">基于 12,580+ 条真实评价</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {reviews.map((review) => (
                    <article key={review.id} className="border-b border-background-200/70 pb-5 last:border-0 last:pb-0">
                      <div className="flex items-start gap-3">
                        <img
                          src={review.avatar}
                          alt={review.user}
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-foreground-900">
                              {review.user}
                            </span>
                            <span className="flex items-center gap-0.5 text-accent-500 text-xs">
                              <i className="ri-star-fill text-[10px]"></i>
                              {review.rating}
                            </span>
                          </div>
                          <p className="text-sm text-foreground-600 leading-relaxed">
                            {review.content}
                          </p>
                          <span className="text-xs text-foreground-400 mt-2 inline-block">
                            {review.date}
                          </span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="relative py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20soft%20romantic%20illustration%20with%20warm%20peach%20and%20rose%20gradient%2C%20floating%20petals%20and%20sparkles%2C%20dreamy%20atmosphere%2C%20soft%20bokeh%2C%20gentle%20anime-inspired%20aesthetic%2C%20clean%20minimal%20composition&width=1600&height=600&seq=article-cta-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/45" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-6">
          <div className="md:w-1/2">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
              还想看更多？
            </h2>
            <p className="text-white/70 text-sm mb-6">
              探索更多同类精彩韩漫，发现你的下一部心头好
            </p>
            <Link
              to={`/category/all`}
              className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium hover:bg-white/30 transition-all cursor-pointer"
            >
              浏览更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}